import { AnimatePresence, motion, useIsPresent } from "motion/react";

import { FloatingSlotContext } from "@/components/floating-slot-context";
import { ChevronRight, X } from "lucide-react";
import { Outlet, useLocation } from "react-router-dom";
import {
  PANEL_CONTENT_OPEN_DELAY,
  fadeInAnimation,
  panelContentAnimation,
  switcherSpring,
} from "@/utils/animations";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { Button } from "./ui/button";
import { SwitcherCountPill } from "./switcher-count-pill";
import { CampaignCountdown } from "@/components/campaign-card/campaign-countdown";
import { CampaignImage } from "@/components/campaign-card/campaign-image";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { campaignKey } from "@/campaigns";
import { useSetWidgetOpen } from "@/hooks/widget";
import { useSwitchableCampaigns } from "@/hooks/use-switchable-campaigns";
import { useWidgetVariant } from "@/contexts/widget-variant";
import { useTranslation } from "react-i18next";
import { useIsDesktop } from "@/hooks/use-media-query";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";

/**
 * One campaign page inside the scroll region. While it is LEAVING it is lifted out of the flow
 * and pinned where it was on screen (`top` compensates for the scroll offset the switch just
 * reset), so the incoming page takes the top of the scroller and rises while this one fades —
 * a real crossfade. (AnimatePresence's own `popLayout` does this by injecting a <style> into
 * document.head, which never reaches the widget's shadow root; without it the two pages stacked
 * for a frame and the new one — tab pill included — jumped up from below.)
 */
const CampaignPage = ({
  delay,
  isInGame,
  children,
}: {
  delay: number;
  isInGame: boolean;
  children: ReactNode;
}) => {
  const isPresent = useIsPresent();
  return (
    <motion.div
      className={cn(
        // Panel-sized (the page owns its scroller, below), so the crossfade's layer is the
        // size of the panel, not of the content: a transform on a content-tall page was a
        // layer thousands of pixels high that phones re-rasterised every frame. A page keeps
        // its scroll position while it leaves, and a new one starts at the top, for free.
        "flex h-full w-full origin-top flex-col",
        !isPresent && "pointer-events-none absolute inset-0"
      )}
      variants={panelContentAnimation}
      custom={{ delay }}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div
        className={cn(
          "touch-scroller relative mx-auto w-full flex-1 overflow-y-auto overscroll-contain p-4 pb-0 ls:p-3 ls:pb-0 ls:pl-6",
          // In-game: flex-child scroll region must be able to shrink
          // (min-h-0) inside the widget column so content scrolls.
          isInGame && "min-h-0"
        )}
      >
        {children}
      </div>
    </motion.div>
  );
};

export const CampaignLayout = () => {
  const location = useLocation();
  // State, not a ref: pages portal into the slot, so they must re-render once it exists.
  const [floatingSlot, setFloatingSlot] = useState<HTMLDivElement | null>(null);
  // The first page waits behind the panel's entrance; every later page (a campaign switch, a
  // route change) rises immediately, crossfading with the one leaving.
  const hasEntered = useRef(false);
  useEffect(() => {
    hasEntered.current = true;
  }, []);
  // The page is keyed on the ROUTE only — an in-game campaign switch keeps the page mounted
  // and swaps the view inside the shell, which resets the scroll itself at the moment the view
  // swaps (while the page is invisible); see CampaignShell.
  const pageKey = location.pathname;
  const variant = useWidgetVariant();
  const isInGame = variant === "in-game";

  return (
    <motion.div
      variants={fadeInAnimation}
      // On-site the route slide (AnimatedOutlet) already brings the page in: a second fade and
      // the page's scale-in on top of it were three stacked entries, and the scale re-rasterised
      // the whole desktop content column every frame — it flickered. In-game there is no route
      // slide, so the layout fade and the page scale ARE the entry (the panel opening).
      initial={isInGame ? "initial" : false}
      animate="animate"
      exit="exit"
      className="relative flex h-full w-full flex-col"
    >
      <div className="relative min-h-0 w-full flex-1">
        {/* Route transitions: the old page fades away in place, the new one scales into place.
            (Campaign switches don't pass through here — see the note above.) */}
        <AnimatePresence initial={isInGame}>
          <CampaignPage
            key={pageKey}
            isInGame={isInGame}
            delay={hasEntered.current ? 0 : PANEL_CONTENT_OPEN_DELAY}
          >
            <FloatingSlotContext.Provider value={floatingSlot}>
              <Outlet />
            </FloatingSlotContext.Provider>
          </CampaignPage>
        </AnimatePresence>
      </div>
      {/* Floating chrome (the mobile opt-in pill) lives here, OUTSIDE the scroll region, pinned
          just above the in-game sheet's tuck (the panel's bottom edge where there is no sheet):
          the same place whatever the page's height or scroll. Pages fill it by portal. */}
      <div
        ref={setFloatingSlot}
        className="pointer-events-none absolute inset-x-0 bottom-[var(--igw-dock-tuck,0px)] z-30 lg:hidden"
      />
    </motion.div>
  );
};

/**
 * The desktop in-game top row: the dropdown campaign switcher (+ its scrim) and the close button.
 * Its own component, MOUNTED only at `lg`: it subscribes to the campaign list and runs a
 * countdown, and kept mounted-but-hidden on phones it re-rendered on every switch and progress
 * push and ticked every second for nothing (phones use the InGameCampaignDock instead).
 */
const DesktopCampaignSwitcherRow = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { closeWidget } = useSetWidgetOpen();
  const {
    selected: selectedCampaign,
    others: otherCampaigns,
    hasMultiple: hasMultipleCampaigns,
    select,
  } = useSwitchableCampaigns();

  // Light-dismiss: a click/tap anywhere outside the switcher (not just on the
  // z-40 overlay below the nav — the nav row itself sits above it) or Escape
  // closes the dropdown. composedPath, not contains: the widget lives in a
  // shadow root and the listener is on the document.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const container = switcherRef.current;
      if (container && !e.composedPath().includes(container)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Capture-phase + only-registered-while-open: this swallows the Escape
      // so the panel's Radix dialog doesn't ALSO close — first Escape closes
      // the dropdown, the next one closes the widget.
      e.stopPropagation();
      setIsOpen(false);
      // Return focus to the trigger — Escape may fire while focus sits on a
      // dropdown row that's about to unmount, which would drop focus to body.
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen]);

  if (!selectedCampaign) return null;

  const handleSelect = (id: string) => {
    select(id);
    setIsOpen(false);
  };
  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsOpen(false)}
            // Desktop dropdown scrim, darkest at the top where the menu is (see .switcher-scrim).
            className="switcher-scrim absolute inset-0 z-40"
          />
        )}
      </AnimatePresence>

      {/* Desktop only. On phones there is no top row at all: close shares the sticky tab row
          inside the content (CampaignShell) and the campaign selector is the InGameCampaignDock,
          rendered OUTSIDE this panel by WidgetContainer. */}
      <nav className="relative z-50 hidden w-full justify-center gap-4 p-2 pb-0 lg:flex lg:justify-between lg:p-4">
        {/* Desktop: the in-place dropdown switcher. */}
        <div className="hidden w-full min-w-0 flex-row items-center justify-between gap-4 lg:flex">
          <div
            ref={switcherRef}
            className="relative h-14 w-full min-w-0 lg:max-w-md"
          >
            <div
              className={cn(
                "absolute inset-x-0 top-0 overflow-hidden rounded-4xl border border-border bg-secondary",
                isOpen ? "notification-shadow" : ""
              )}
            >
              <button
                ref={triggerRef}
                type="button"
                onClick={() => hasMultipleCampaigns && setIsOpen((o) => !o)}
                disabled={!hasMultipleCampaigns}
                className={cn(
                  "flex w-full items-center gap-3 p-2",
                  hasMultipleCampaigns ? "cursor-pointer" : "cursor-default"
                )}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedCampaign.campaign.id}
                    initial={{ scaleY: 0.9, y: 8 }}
                    animate={{ scaleY: 1, y: 0, originY: 1 }}
                    transition={switcherSpring}
                  >
                    <CampaignImage
                      campaign={selectedCampaign.campaign}
                      hideTitle
                      hideDuration
                      className={cn(
                        "size-10 shrink-0 rounded-full lg:size-10",
                        selectedCampaign.campaign.status === "FI" &&
                          "opacity-70 grayscale"
                      )}
                    />
                  </motion.div>
                </AnimatePresence>
                <Typography
                  tag="span"
                  className={cn(
                    "flex-1 truncate text-center font-bold tracking-wide",
                    selectedCampaign.campaign.status === "FI"
                      ? "text-muted-foreground"
                      : "text-primary"
                  )}
                >
                  {campaignDisplayTitle(selectedCampaign.campaign, t)}
                </Typography>
                {hasMultipleCampaigns && (
                  // Same pill as the mobile dock, unfolding downward here.
                  <SwitcherCountPill
                    count={otherCampaigns.length}
                    open={isOpen}
                    unfolds="down"
                  />
                )}
              </button>

              <AnimatePresence initial={false}>
                {isOpen && otherCampaigns.length > 0 && (
                  <motion.ul
                    // Height alone — the old parallel scaleY double-animated
                    // the same axis (squashing rows) for no visual gain. The
                    // bouncy spring is intentional (product feel); the per-frame
                    // height reflow it causes is contained to this absolutely-
                    // positioned dropdown and measures <1ms.
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{
                      height: 0,
                      opacity: 0,
                      transition: { duration: 0.1 },
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 15,
                      mass: 0.5,
                    }}
                    className="flex flex-col overflow-hidden"
                  >
                    {otherCampaigns.map((entry) => {
                      const id = campaignKey(entry.campaign);
                      return (
                        <li
                          key={id}
                          className="border-t border-border/40 bg-background"
                        >
                          <button
                            type="button"
                            onClick={() => handleSelect(id)}
                            className={cn(
                              "flex w-full cursor-pointer items-center gap-3 p-2 text-left transition",
                              "hover:bg-background/30"
                            )}
                          >
                            <CampaignImage
                              campaign={entry.campaign}
                              hideTitle
                              hideDuration
                              className={cn(
                                "size-10 shrink-0 rounded-full lg:size-10",
                                entry.campaign.status === "FI" &&
                                  "opacity-70 grayscale"
                              )}
                            />
                            <Typography
                              tag="span"
                              className={cn(
                                "flex-1 truncate",
                                entry.campaign.status === "FI"
                                  ? "text-muted-foreground"
                                  : "text-foreground"
                              )}
                            >
                              {campaignDisplayTitle(entry.campaign, t)}
                            </Typography>
                            <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                          </button>
                        </li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </div>
          <CampaignCountdown
            campaign={selectedCampaign.campaign}
            variant="header"
            className="hidden shrink-0 lg:flex"
          />
        </div>
        <div className="relative hidden flex-row items-center justify-end gap-4 lg:-top-1 lg:flex">
          <Button
            size="icon"
            onClick={closeWidget}
            variant={"secondary"}
            className="size-10"
          >
            <X className="size-6 text-muted-foreground" />
          </Button>
        </div>
      </nav>
    </>
  );
};

export const InGameCampaignNavigation = () => {
  const isDesktop = useIsDesktop();
  return (
    <div className="relative flex h-full w-full flex-col">
      {isDesktop && <DesktopCampaignSwitcherRow />}
      {/* `flex-1 min-h-0` so CampaignLayout takes only the space left after the desktop top row.
          Without this, CampaignLayout's inner `h-full` expands to the full parent height
          (ignoring the row as a flex sibling), making the page un-scrollable to the bottom. */}
      <div className="min-h-0 flex-1">
        <CampaignLayout />
      </div>
    </div>
  );
};
